/**
 * Die **Tönung** von Code: aus Zeilen werden Token, aus Token wird Farbe
 * (ADR-0026).
 *
 * Eine reine Funktion, kein React, kein Zustand außerhalb der Eingabe. Sie
 * bekommt einen **Strom** von Zeilen und gibt je Zeile eine Folge von Token; der
 * Zustand am Zeilenende — offene Zeichenkette, offener Blockkommentar, offenes
 * Heredoc, Austritt aus `<?php` — läuft in die nächste Zeile desselben Stroms und
 * ausdrücklich nicht darüber hinaus.
 *
 * **Fünf Rollen und sonst nichts.** Alles übrige — Bezeichner, Funktions- und
 * Klassennamen, Typen, Klammern, Operatoren — bleibt `plain` und damit
 * `--text-body`. Keine semantische Analyse: Der Tokenizer weiß nicht, was ein
 * Funktionsname ist, und soll es nicht wissen.
 *
 * **Erkannt wird die Sprache an der Endung**, gegen eine geschlossene Liste. Eine
 * unbekannte Endung heißt: keine Tönung. Geraten wird nichts — eine falsch
 * geratene Sprache erzeugt plausible Farben an den falschen Stellen, und das fällt
 * niemandem auf.
 *
 * **Eine Obergrenze für große Dateien steht hier nicht, weil die Messung sie nicht
 * verlangt.** Eine synthetische PHP-Datei mit 5.000 Zeilen zu tönen kostet 7 ms
 * (drei Läufe: 11,3 / 7,1 / 6,8 ms). Sie zu zeichnen kostet in jsdom 5,6 bis 6,0 s
 * ungetönt und 6,0 bis 6,2 s getönt — die Tönung legt also rund 5 % auf einen Preis,
 * den das Zeichnen jeder Zeile als eigener Knopf ohne Virtualisierung schon vorher
 * verlangte. Wer hier eine Grenze braucht, braucht sie gegen das Zeichnen und nicht
 * gegen die Tönung; kommt sie, ist sie sichtbar und nicht stumm (ADR-0026).
 */

import type { DiffLine } from "../../api/client";

/**
 * Die geschlossene Menge der Rollen. Englisch, weil das Glossar sie nicht führt
 * (ADR-0009).
 */
export type Role = "keyword" | "string" | "number" | "comment" | "variable" | "plain";

/** Ein Stück Zeile und die Rolle, in der es dort steht. */
export interface Token {
  readonly text: string;
  readonly role: Role;
}

/** Die Sprachen, die getönt werden — und `none` für alles übrige. */
export type Language = "php" | "xml" | "none";

/**
 * Endung → Sprache. `.project`, `.buildpath` und `.prefs` sind die Projektdateien
 * von Eclipse, die der Bestand führt.
 */
const BY_EXTENSION: Record<string, Language> = {
  php: "php",
  xml: "xml",
  html: "xml",
  htm: "xml",
  project: "xml",
  buildpath: "xml",
  prefs: "xml",
};

/**
 * Die Sprache einer Datei, an ihrer Endung gelesen.
 *
 * Eine Punktdatei ohne weiteren Punkt — Eclipse legt `.project` und `.buildpath`
 * so ab — trägt ihre Endung im ganzen Namen; deshalb zählt hier der **letzte**
 * Punkt und nicht der erste.
 */
export function languageOf(path: string): Language {
  const name = path.slice(path.lastIndexOf("/") + 1);
  const dot = name.lastIndexOf(".");

  if (dot === -1) {
    return "none";
  }

  return BY_EXTENSION[name.slice(dot + 1).toLowerCase()] ?? "none";
}

/**
 * Ein Strom von Zeilen, getönt — je Zeile eine Folge von Token, in der Reihenfolge
 * der Zeichen und ohne dass eines verloren geht.
 */
export function highlight(lines: readonly string[], language: Language): Token[][] {
  if (language === "php") {
    return highlightPhp(lines);
  }

  if (language === "xml") {
    return highlightXml(lines);
  }

  return lines.map((line) => [{ text: line, role: "plain" as const }]);
}

/**
 * Die Zeilen eines Diff-Abschnitts, getönt — **je Seite ein Strom**.
 *
 * Ein Hunk ist ein Bruchstück zweier verschiedener Dateiversionen. Wer den Zustand
 * über entfernte und hinzugefügte Zeilen gemeinsam führte, tönte gegen eine Datei,
 * die es nicht gibt: Ein eröffneter Blockkommentar auf einer entfernten Zeile
 * färbte die folgende hinzugefügte. Getönt wird deshalb zweimal — die alte Seite
 * (entfernt und unverändert) und die neue (hinzugefügt und unverändert) —, und jede
 * Zeile bekommt das Ergebnis ihrer eigenen Seite. Die unveränderten laufen in
 * beiden Strömen mit, damit der Zustand auf beiden Seiten stimmt; gezeichnet wird
 * für sie die neue Seite, denn sie ist der Stand, um den es geht.
 *
 * **An der Hunk-Grenze bleibt das blind** (ADR-0026): Was zwischen zwei
 * Abschnitten fehlt, fehlt auch dem Tokenizer.
 */
export function tintDiff(lines: readonly DiffLine[], language: Language): Token[][] {
  const tinted: Token[][] = lines.map(() => []);

  fillSide(lines, language, tinted, "removed");
  fillSide(lines, language, tinted, "added");

  return tinted;
}

/**
 * Tönt eine Seite des Hunks und schreibt das Ergebnis dorthin, wo diese Seite
 * zuständig ist: die eigenen Zeilen immer, die unveränderten nur auf der neuen
 * Seite — sie kommt später und behält das letzte Wort.
 */
function fillSide(
  lines: readonly DiffLine[],
  language: Language,
  tinted: Token[][],
  own: DiffLine["kind"],
): void {
  const mine: { at: number; kind: DiffLine["kind"]; text: string }[] = [];

  lines.forEach((line, index) => {
    if (line.kind === own || line.kind === "context") {
      mine.push({ at: index, kind: line.kind, text: line.text });
    }
  });

  const stream = highlight(
    mine.map((entry) => entry.text),
    language,
  );

  mine.forEach((entry, position) => {
    const tokens = stream[position];

    if (tokens !== undefined && (entry.kind === own || own === "added")) {
      tinted[entry.at] = tokens;
    }
  });
}

/**
 * Hängt Text an die Token einer Zeile an.
 *
 * Zwei benachbarte Stücke derselben Rolle werden **eine** Token: Ein Leerzeichen,
 * ein Operator und ein Klammerpaar hintereinander sind kein Grund für drei Spans,
 * und die Zusicherungen sprechen dann über die Zeile und nicht über die
 * Schrittweite des Tokenizers.
 */
function emit(tokens: Token[], text: string, role: Role): void {
  if (text === "") {
    return;
  }

  const last = tokens[tokens.length - 1];

  if (last !== undefined && last.role === role) {
    tokens[tokens.length - 1] = { text: last.text + text, role };

    return;
  }

  tokens.push({ text, role });
}

/** Der Anfang eines Namens in PHP — Buchstabe, Unterstrich, oder ein Byte über 127. */
const NAME_START = /[A-Za-z_\u0080-\uffff]/;

/** Und was darin weitergeht. */
const NAME_PART = /[A-Za-z0-9_\u0080-\uffff]/;

/** Der Name ab dieser Stelle, oder die leere Zeichenkette. */
function nameAt(line: string, from: number): string {
  if (!NAME_START.test(line.charAt(from))) {
    return "";
  }

  let end = from + 1;

  while (end < line.length && NAME_PART.test(line.charAt(end))) {
    end += 1;
  }

  return line.slice(from, end);
}

/**
 * Die Zahl ab dieser Stelle. Angesehen wird sie nur dort, wo kein Name steht —
 * sonst wäre die `5` in `md5` eine Zahl.
 */
const NUMBER =
  /0[xX][0-9a-fA-F_]+|0[bB][01_]+|0[oO][0-7_]+|(?:\d[\d_]*)?\.\d[\d_]*(?:[eE][+-]?\d[\d_]*)?|\d[\d_]*\.?(?:[eE][+-]?\d[\d_]*)?/y;

function numberAt(line: string, from: number): string | null {
  NUMBER.lastIndex = from;

  const found = NUMBER.exec(line);

  return found === null || found[0] === "" ? null : found[0];
}

/** Bis wohin eine Zeichenkette reicht, und ob sie in dieser Zeile schließt. */
function endOfQuote(
  line: string,
  from: number,
  quote: string,
  escapes: boolean,
): { end: number; closed: boolean } {
  let index = from;

  while (index < line.length) {
    if (escapes && line.charAt(index) === "\\") {
      index += 2;
      continue;
    }

    if (line.charAt(index) === quote) {
      return { end: index + 1, closed: true };
    }

    index += 1;
  }

  return { end: line.length, closed: false };
}

/** Bis wohin ein Abschnitt reicht, der mit einer festen Marke endet. */
function endOfRun(line: string, from: number, mark: string): { end: number; closed: boolean } {
  const at = line.indexOf(mark, from);

  return at === -1 ? { end: line.length, closed: false } : { end: at + mark.length, closed: true };
}

/* -------------------------------------------------------------------- PHP -- */

/**
 * Die reservierten Wörter von PHP — ohne die **Typnamen**: `int`, `float`,
 * `string`, `bool`, `void`, `never`, `mixed`, `iterable`, `object`, `self` und
 * `parent` bleiben ungetönt, weil ADR-0026 Typen ausdrücklich bei `--text-body`
 * lässt.
 */
const PHP_KEYWORDS = new Set([
  "abstract",
  "and",
  "array",
  "as",
  "break",
  "case",
  "catch",
  "class",
  "clone",
  "const",
  "continue",
  "declare",
  "default",
  "die",
  "do",
  "echo",
  "else",
  "elseif",
  "empty",
  "enddeclare",
  "endfor",
  "endforeach",
  "endif",
  "endswitch",
  "endwhile",
  "enum",
  "eval",
  "exit",
  "extends",
  "final",
  "finally",
  "fn",
  "for",
  "foreach",
  "function",
  "global",
  "goto",
  "if",
  "implements",
  "include",
  "include_once",
  "instanceof",
  "insteadof",
  "interface",
  "isset",
  "list",
  "match",
  "namespace",
  "new",
  "or",
  "print",
  "private",
  "protected",
  "public",
  "readonly",
  "require",
  "require_once",
  "return",
  "static",
  "switch",
  "throw",
  "trait",
  "try",
  "unset",
  "use",
  "var",
  "while",
  "xor",
  "yield",
]);

/** Die Konstanten, die PHP selbst mitbringt — Rolle „Zahl, Konstante". */
const PHP_CONSTANTS = new Set(["true", "false", "null"]);

/** Wo der Tokenizer beim Zeilenwechsel steht. */
type PhpState =
  | { kind: "text" }
  | { kind: "code" }
  | { kind: "quote"; quote: string }
  | { kind: "block" }
  | { kind: "heredoc"; label: string };

/** Die Eröffnung eines Heredocs oder Nowdocs, samt seinem Bezeichner. */
const HEREDOC =
  /^<<<[ \t]*(?:'([A-Za-z_][A-Za-z0-9_]*)'|"([A-Za-z_][A-Za-z0-9_]*)"|([A-Za-z_][A-Za-z0-9_]*))/;

/**
 * PHP, im schlechtesten Fall in zwei Läufen.
 *
 * Der Anfang eines Stroms ist nicht der Anfang einer Datei: Ein Diff-Abschnitt
 * steht mitten im Code, und wer dort außerhalb von `<?php` anfängt, tönt jeden
 * Abschnitt einer PHP-Datei als Fließtext. Angefangen wird deshalb **im Code** —
 * und wenn dabei eine Eröffnungsmarke auftaucht, die nicht in einer Zeichenkette
 * und nicht in einem Kommentar steht, dann begann dieser Strom eben doch außerhalb;
 * dann läuft er noch einmal von vorn, diesmal als Text.
 *
 * Der erste Lauf bricht an dieser Marke ab, sein Ergebnis wird verworfen. Bei einer
 * ganzen Datei steht sie in der ersten Zeile, dort kostet der Abbruch nichts.
 */
function highlightPhp(lines: readonly string[]): Token[][] {
  return runPhp(lines, { kind: "code" }) ?? runPhp(lines, { kind: "text" }) ?? [];
}

/**
 * Ein Lauf über den Strom. `null` heißt: Im Code stand eine Eröffnungsmarke, dieser
 * Strom beginnt außerhalb von `<?php`.
 */
function runPhp(lines: readonly string[], initial: PhpState): Token[][] | null {
  const tinted: Token[][] = [];
  let state = initial;

  for (const line of lines) {
    const tokens: Token[] = [];
    const next = scanPhpLine(line, tokens, state);

    if (next === null) {
      return null;
    }

    tinted.push(tokens);
    state = next;
  }

  return tinted;
}

function scanPhpLine(line: string, tokens: Token[], initial: PhpState): PhpState | null {
  let state = initial;
  let index = 0;

  while (index < line.length) {
    if (state.kind === "text") {
      const opening = openTagIn(line, index);

      if (opening === null) {
        emit(tokens, line.slice(index), "plain");
        index = line.length;
        continue;
      }

      emit(tokens, line.slice(index, opening.index), "plain");
      emit(tokens, opening.tag, "keyword");
      index = opening.index + opening.tag.length;
      state = { kind: "code" };
      continue;
    }

    if (state.kind === "quote") {
      const { end, closed } = endOfQuote(line, index, state.quote, true);

      emit(tokens, line.slice(index, end), "string");
      index = end;

      if (closed) {
        state = { kind: "code" };
      }

      continue;
    }

    if (state.kind === "block") {
      const { end, closed } = endOfRun(line, index, "*/");

      emit(tokens, line.slice(index, end), "comment");
      index = end;

      if (closed) {
        state = { kind: "code" };
      }

      continue;
    }

    if (state.kind === "heredoc") {
      // Der Abschlussbezeichner steht am Zeilenanfang, seit PHP 7.3 auch
      // eingerückt. Steht der Strom mitten in der Zeile, ist es der Rumpf.
      const end = index === 0 ? closesHeredoc(line, state.label) : null;

      if (end === null) {
        emit(tokens, line.slice(index), "string");
        index = line.length;
        continue;
      }

      emit(tokens, line.slice(0, end), "string");
      index = end;
      state = { kind: "code" };
      continue;
    }

    const char = line.charAt(index);

    if (char === "?" && line.charAt(index + 1) === ">") {
      emit(tokens, "?>", "keyword");
      index += 2;
      state = { kind: "text" };
      continue;
    }

    // Eine Eröffnungsmarke mitten im Code: Dieser Strom fing außerhalb an.
    if (openTagAt(line, index) !== null) {
      return null;
    }

    if (char === "/" && line.charAt(index + 1) === "/") {
      index = lineComment(line, index, tokens);
      continue;
    }

    // `#[` ist ein Attribut und kein Kommentar.
    if (char === "#") {
      if (line.charAt(index + 1) === "[") {
        emit(tokens, "#[", "plain");
        index += 2;
        continue;
      }

      index = lineComment(line, index, tokens);
      continue;
    }

    if (char === "/" && line.charAt(index + 1) === "*") {
      const { end, closed } = endOfRun(line, index + 2, "*/");

      emit(tokens, line.slice(index, end), "comment");
      index = end;
      state = closed ? { kind: "code" } : { kind: "block" };
      continue;
    }

    if (char === "'" || char === '"' || char === "`") {
      const { end, closed } = endOfQuote(line, index + 1, char, true);

      emit(tokens, line.slice(index, end), "string");
      index = end;
      state = closed ? { kind: "code" } : { kind: "quote", quote: char };
      continue;
    }

    if (char === "<" && line.startsWith("<<<", index)) {
      const opener = HEREDOC.exec(line.slice(index));

      if (opener !== null) {
        emit(tokens, opener[0], "string");
        index += opener[0].length;
        state = { kind: "heredoc", label: opener[1] ?? opener[2] ?? opener[3] ?? "" };
        continue;
      }
    }

    if (char === "$") {
      const name = nameAt(line, index + 1);

      emit(tokens, `$${name}`, name === "" ? "plain" : "variable");
      index += 1 + name.length;
      continue;
    }

    const name = nameAt(line, index);

    if (name !== "") {
      const lowered = name.toLowerCase();

      emit(
        tokens,
        name,
        PHP_CONSTANTS.has(lowered) ? "number" : PHP_KEYWORDS.has(lowered) ? "keyword" : "plain",
      );
      index += name.length;
      continue;
    }

    const number = numberAt(line, index);

    if (number !== null) {
      emit(tokens, number, "number");
      index += number.length;
      continue;
    }

    emit(tokens, char, "plain");
    index += 1;
  }

  return state;
}

/**
 * Ein Zeilenkommentar, geschrieben bis zum Zeilenende — oder bis `?>`, denn das
 * beendet in PHP auch einen Zeilenkommentar.
 */
function lineComment(line: string, from: number, tokens: Token[]): number {
  const at = line.indexOf("?>", from);
  const end = at === -1 ? line.length : at;

  emit(tokens, line.slice(from, end), "comment");

  return end;
}

/** Die nächste Eröffnungsmarke in dieser Zeile, ab dieser Stelle. */
function openTagIn(line: string, from: number): { index: number; tag: string } | null {
  let index = from;

  while (index < line.length) {
    const at = line.indexOf("<?", index);

    if (at === -1) {
      return null;
    }

    const tag = openTagAt(line, at);

    if (tag !== null) {
      return { index: at, tag };
    }

    index = at + 2;
  }

  return null;
}

/**
 * Die Eröffnungsmarke genau an dieser Stelle: `<?php` samt folgendem Leerraum oder
 * Zeilenende, oder `<?=`. Die kurze Marke `<?` ist Konfigurationssache und wird
 * nicht geraten.
 */
function openTagAt(line: string, at: number): string | null {
  if (line.startsWith("<?=", at)) {
    return "<?=";
  }

  const long = line.slice(at, at + 5);

  if (long.toLowerCase() === "<?php") {
    const after = line.charAt(at + 5);

    if (after === "" || /\s/.test(after)) {
      return long;
    }
  }

  return null;
}

/** Wo der Abschlussbezeichner eines Heredocs endet, wenn diese Zeile ihn trägt. */
function closesHeredoc(line: string, label: string): number | null {
  const indent = /^[ \t]*/.exec(line)?.[0].length ?? 0;

  if (!line.startsWith(label, indent)) {
    return null;
  }

  const after = line.charAt(indent + label.length);

  if (after !== "" && /[A-Za-z0-9_]/.test(after)) {
    return null;
  }

  return indent + label.length;
}

/* -------------------------------------------------------------------- XML -- */

/** Wo der Tokenizer beim Zeilenwechsel steht. */
type XmlState =
  | { kind: "text" }
  | { kind: "tag" }
  | { kind: "quote"; quote: string }
  | { kind: "comment" }
  | { kind: "cdata" };

/** Ein Name in XML: Doppelpunkt, Punkt und Strich gehören dazu. */
const XML_NAME_START = /[A-Za-z_:]/;

const XML_NAME_PART = /[A-Za-z0-9_:.-]/;

/**
 * XML — und mit ihm HTML und die Projektdateien von Eclipse.
 *
 * Getönt werden **Elementnamen**, **Attributwerte** und **Kommentare**. Die
 * Attributnamen bleiben ungetönt: Sie sind Bezeichner, und die bleiben es auch
 * hier. Ein Abschnitt in CDATA ist eine Zeichenkette und keine Auszeichnung —
 * sonst eröffnete ein `<` darin ein Element, das nie schließt.
 */
function highlightXml(lines: readonly string[]): Token[][] {
  const tinted: Token[][] = [];
  let state: XmlState = { kind: "text" };

  for (const line of lines) {
    const tokens: Token[] = [];

    state = scanXmlLine(line, tokens, state);
    tinted.push(tokens);
  }

  return tinted;
}

function xmlNameAt(line: string, from: number): string {
  if (!XML_NAME_START.test(line.charAt(from))) {
    return "";
  }

  let end = from + 1;

  while (end < line.length && XML_NAME_PART.test(line.charAt(end))) {
    end += 1;
  }

  return line.slice(from, end);
}

function scanXmlLine(line: string, tokens: Token[], initial: XmlState): XmlState {
  let state = initial;
  let index = 0;

  while (index < line.length) {
    if (state.kind === "comment" || state.kind === "cdata") {
      const isComment = state.kind === "comment";
      const { end, closed } = endOfRun(line, index, isComment ? "-->" : "]]>");

      emit(tokens, line.slice(index, end), isComment ? "comment" : "string");
      index = end;

      if (closed) {
        state = { kind: "text" };
      }

      continue;
    }

    if (state.kind === "quote") {
      const { end, closed } = endOfQuote(line, index, state.quote, false);

      emit(tokens, line.slice(index, end), "string");
      index = end;

      if (closed) {
        state = { kind: "tag" };
      }

      continue;
    }

    if (state.kind === "tag") {
      const char = line.charAt(index);
      const closer = closerAt(line, index);

      if (closer !== null) {
        emit(tokens, closer, "plain");
        index += closer.length;
        state = { kind: "text" };
        continue;
      }

      if (char === '"' || char === "'") {
        const { end, closed } = endOfQuote(line, index + 1, char, false);

        emit(tokens, line.slice(index, end), "string");
        index = end;
        state = closed ? { kind: "tag" } : { kind: "quote", quote: char };
        continue;
      }

      emit(tokens, char, "plain");
      index += 1;
      continue;
    }

    const at = line.indexOf("<", index);

    if (at === -1) {
      emit(tokens, line.slice(index), "plain");
      index = line.length;
      continue;
    }

    emit(tokens, line.slice(index, at), "plain");

    if (line.startsWith("<!--", at)) {
      const { end, closed } = endOfRun(line, at + 4, "-->");

      emit(tokens, line.slice(at, end), "comment");
      index = end;
      state = closed ? { kind: "text" } : { kind: "comment" };
      continue;
    }

    if (line.startsWith("<![CDATA[", at)) {
      const { end, closed } = endOfRun(line, at + 9, "]]>");

      emit(tokens, line.slice(at, end), "string");
      index = end;
      state = closed ? { kind: "text" } : { kind: "cdata" };
      continue;
    }

    // `<?xml`, `<!DOCTYPE`, `</name` und `<name` — die Marke gehört zum Namen, denn
    // zusammen sind sie das, was die Auszeichnung ausmacht.
    const mark = markAt(line, at);
    const name = xmlNameAt(line, at + mark.length);

    if (name === "") {
      emit(tokens, "<", "plain");
      index = at + 1;
      continue;
    }

    emit(tokens, mark + name, "keyword");
    index = at + mark.length + name.length;
    state = { kind: "tag" };
  }

  return state;
}

/** Die Marke, mit der eine Auszeichnung anfängt. */
function markAt(line: string, at: number): string {
  const two = line.slice(at, at + 2);

  return two === "</" || two === "<?" || two === "<!" ? two : "<";
}

/** Womit eine Auszeichnung an dieser Stelle schließt, oder `null`. */
function closerAt(line: string, at: number): string | null {
  if (line.charAt(at) === ">") {
    return ">";
  }

  const two = line.slice(at, at + 2);

  return two === "/>" || two === "?>" ? two : null;
}
